import { Divider, Empty, Flex, Loading, Space } from '@/components';
import { useUploadAttachments } from '@/features/attachment/_hooks/attachment';
import { useMe } from '@/features/authentication/_hooks/me';
import { useParseUsersByIds } from '@/features/tenant-manager/user/_hooks/user';
import { useMessage } from '@/providers';
import { datetimeFormat } from '@/utils';
import dayjs from 'dayjs';
import {
  type FC,
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';

import {
  useChangeCommentReadStatus,
  useComment,
} from '@/features/comment/hooks/comment';
import { useDeleteReply, useUpdateReply } from '@/features/comment/hooks/reply';
import { useCommentStackSiderView } from '@/features/comment/hooks/stack-sider-view';
import type { ReplyFormSchema } from '@/features/comment/models/reply';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { CommentDetailsUnreadDivider } from '../../common/CommentDetailsUnreadDivider';
import { CommentPublishTag } from '../../common/CommentPublishTag';
import { PinpointContext, PinpointDisplay, usePinpoint } from '../../pinpoint';
import { CommentReply } from '../../reply';
import { CommentDetailsCard } from './CommentDetailsCard';
import { CommentDetailsFooterWrapper } from './CommentDetailsFooterWrapper';
import { CommentDetailsReplyForm } from './CommentDetailsReplyForm';

dayjs.extend(isSameOrAfter);

export const CommentDetails: FC = () => {
  const { t } = useTranslation();
  const stackSiderView = useCommentStackSiderView();

  const siderConfig = stackSiderView.lastItem?.config;

  invariant(siderConfig?.viewType === 'comment-details', t('unreachable-code'));

  const { openMessage } = useMessage();
  const pinpointContext = useContext(PinpointContext);

  const comment = useComment(siderConfig.id);
  const me = useMe();

  const commentsEndRef = useRef<HTMLDivElement | null>(null);
  const isAfterFirstRender = useRef<boolean>(false);
  // The line of unread is determined by the date and time stored at the first load
  const [initialReadStatusChangedAt, setInitialReadStatusChangedAt] = useState<
    string | null
  >(null);

  const { uploadAttachments } = useUploadAttachments();
  const { mutateAsync: deleteReply } = useDeleteReply();
  const { mutateAsync: updateReply } = useUpdateReply();
  const { mutateAsync: changeCommentReadStatus } = useChangeCommentReadStatus(
    siderConfig.id,
  );

  const handleUpdateReply = async (
    replyId: string,
    { content, fileEntries }: z.infer<typeof ReplyFormSchema>,
  ) => {
    if (!comment.data) {
      return;
    }

    const attachmentIds: string[] =
      fileEntries?.flatMap((entry) =>
        entry.attachment ? [entry.attachment.id] : [],
      ) ?? [];

    const files = fileEntries?.flatMap((entry) =>
      entry.file ? [entry.file] : [],
    );

    if (files?.length) {
      const uploadedAttachmentIds = await uploadAttachments(files);
      if (uploadedAttachmentIds?.length) {
        attachmentIds.push(...uploadedAttachmentIds);
      }
    }

    try {
      await updateReply({
        id: replyId,
        content,
        attachmentIds,
        commentId: comment.data.id,
      });
      await openMessage('success', t('message-update-completed'));
    } catch {
      await openMessage('error', t('message-update-failed'));
    }
  };

  const handleDeleteReply = async (replyId: string) => {
    if (!comment.data) {
      return;
    }

    try {
      await deleteReply({
        id: replyId,
        commentId: comment.data.id,
      });
      await openMessage('success', t('message-delete-completed'));
    } catch {
      await openMessage('error', t('message-delete-failed'));
    }
  };

  useEffect(() => {
    if (
      comment.data?.readStatusChangedAt &&
      initialReadStatusChangedAt === null
    ) {
      setInitialReadStatusChangedAt(comment.data.readStatusChangedAt);
    }
  }, [comment.data, initialReadStatusChangedAt]);

  const unreadReplyIndex = useMemo(() => {
    if (!comment.data) {
      return undefined;
    }

    const unreadReplyIndex = comment.data?.replies?.findIndex(
      (reply) =>
        initialReadStatusChangedAt &&
        dayjs(reply.createdAt).isSameOrAfter(
          dayjs(initialReadStatusChangedAt),
        ) &&
        reply.userId !== me.data?.id,
    );
    return unreadReplyIndex;
  }, [comment, me.data, initialReadStatusChangedAt]);

  const userIdsForParse =
    comment.data?.replies?.map((reply) => reply.userId) ?? [];
  const { parseUserById } = useParseUsersByIds(userIdsForParse);

  useEffect(() => {
    if (comment.data) {
      if (!isAfterFirstRender.current && comment.data.readStatus === 'unread') {
        changeCommentReadStatus({
          readStatus: 'read',
        });
      }
      isAfterFirstRender.current = true;
    }
  }, [changeCommentReadStatus, comment.data]);

  useEffect(() => {
    if (comment.data?.replies && commentsEndRef.current) {
      commentsEndRef.current.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [comment.data?.replies]);

  useEffect(() => {
    if (comment.isError) {
      pinpointContext?.setPinpoint(null);

      stackSiderView.pop();
    }
  }, [comment.isError, pinpointContext, stackSiderView]);

  const { scrollToPinpoint } = usePinpoint();

  useEffect(() => {
    const pinpoint = comment.data?.pinpoint;
    if (pinpoint && pinpointContext) {
      pinpointContext.setPinpoint(pinpoint);
      scrollToPinpoint(pinpoint);
    }
  }, [comment.data?.pinpoint, scrollToPinpoint, pinpointContext]);

  if (comment.isPending) {
    return <Loading />;
  }

  if (!comment.data) {
    return null;
  }

  return (
    <Flex vertical justify="space-between" style={{ height: '100%' }}>
      <Flex vertical gap={20}>
        <Flex vertical gap="middle">
          <CommentDetailsCard
            type="comment"
            assigneeIds={comment.data.assigneeIds}
            authorId={comment.data.authorId}
            content={comment.data.content}
            createdAt={comment.data.createdAt}
            label={comment.data.label}
            publishType={comment.data.publishType}
            readStatus={comment.data.readStatus}
            relatedUserIds={comment.data.relatedUserIds}
            sharedOrganizationIds={comment.data.sharedOrganizationIds}
            tagTargets={comment.data.tagTargets}
            attachments={comment.data.attachments}
          />
        </Flex>
        <Flex vertical gap={24}>
          {comment.data.replies?.length === 0 ? (
            <Empty description={t('no-data')} />
          ) : (
            comment.data.replies?.map((reply, index) => {
              const replier = parseUserById(reply.userId);

              const canEdit = reply.userId === me.data?.id;

              return (
                <Fragment key={reply.id}>
                  {index === unreadReplyIndex && (
                    <CommentDetailsUnreadDivider />
                  )}
                  <CommentReply
                    id={reply.id}
                    content={reply.content}
                    createdAt={datetimeFormat({
                      datetime: reply.createdAt,
                      format: 'YYYY/MM/DD HH:mm',
                    })}
                    replier={replier}
                    editable={canEdit}
                    attachments={reply.attachments}
                    {...(canEdit && {
                      onDelete: handleDeleteReply,
                      onUpdate: handleUpdateReply,
                    })}
                  />
                </Fragment>
              );
            })
          )}
        </Flex>
        <div ref={commentsEndRef} style={{ height: 24 }} />
      </Flex>

      <CommentDetailsFooterWrapper>
        <Divider />
        <Space>
          <CommentPublishTag publishType={comment.data.publishType} />
          {comment.data.pinpoint && (
            <PinpointDisplay
              pinpoint={comment.data.pinpoint}
              commentId={siderConfig.id}
              objectId={comment.data.objectId}
              showIcon
              color="secondary"
              navigateOnClick
            />
          )}
        </Space>
        <CommentDetailsReplyForm commentId={siderConfig.id} />
      </CommentDetailsFooterWrapper>
    </Flex>
  );
};
